import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { Eye, EyeOff, Fingerprint } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { isNative } from "../lib/admob";
import { startNativeGoogleSignIn } from "../lib/nativeAuth";
import { isGoogleOneTapAvailable, signInWithGoogleNative } from "../lib/googleOneTap";
import { setSessionToken, getSessionToken, getSessionDiag } from "../lib/sessionStore";
import { biometricAvailable, biometricEnabled, enableBiometric, biometricLogin } from "../lib/biometric";
import { useTranslation } from "react-i18next";

// Iter77 — Build marker so testers can confirm which JS bundle is
// actually running inside the Capacitor WebView. Stamp this string on
// every iteration; if the on-device login screen doesn't match, the
// APK is serving a stale cached bundle.
const BUILD_TAG = "i142-countdown-voice";

export default function Login() {
  const { t } = useTranslation();
  const { refresh, user, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const sessionExpired = params.get("expired") === "1";
  // Modes: "login" | "signup" | "reset"
  const [mode, setMode] = useState(params.get("mode") === "signup" ? "signup" : "login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [name, setName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  // Google sign-in is available on BOTH web (Emergent redirect flow) and
  // native (Capacitor Browser plugin + zenjong:// deep-link callback). The
  // native helper (`startNativeGoogleSignIn`) toasts a fallback message if
  // the deep-link doesn't return — so the worst case is a clear error
  // rather than a hidden auth method.
  const googleAvailable = true;

  // Biometric login (native only). When the user previously logged in and
  // enabled biometrics, tapping "I already have an account" lands here and
  // we show a "Log in with fingerprint / face" button.
  //
  // Iter96 — We NO LONGER auto-fire the prompt on mount. Auto-firing during
  // the route-mount lifecycle crashes BiometricPrompt on some Android
  // devices (FragmentActivity not fully RESUMED → native process dies →
  // launcher shows a "clear cache" dialog). The prompt now only runs on an
  // explicit button tap, which is the documented-safe pattern.
  const [bioReady, setBioReady] = useState(false);

  useEffect(() => {
    if (!loading && user) navigate("/dashboard", { replace: true });
  }, [loading, user, navigate]);

  const doBiometricLogin = async () => {
    const res = await biometricLogin(t("auth.biometric_reason", "Log in to Fitjong"));
    if (res.ok && res.token) {
      await setSessionToken(res.token);
      try { await refresh(); } catch (_) {}
      navigate("/dashboard");
    } else if (res.reason === "no-credentials") {
      // Saved token vanished (e.g. session revoked) — fall back silently.
      setBioReady(false);
    }
    // "cancelled" / "web" → do nothing, normal form stays available.
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (mode !== "login") return;
      const [{ available }, enabled] = await Promise.all([
        biometricAvailable(),
        biometricEnabled(),
      ]);
      if (cancelled || !available || !enabled) return;
      setBioReady(true);
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (mode === "reset") {
        if (password !== confirmPassword) {
          toast.error(t("auth.passwords_dont_match"));
          setSubmitting(false);
          return;
        }
        await api.post("/auth/reset-password", { email, new_password: password });
        toast.success(t("auth.password_updated"));
        setMode("login");
        setPassword("");
        setConfirmPassword("");
      } else {
        const url = mode === "signup" ? "/auth/signup" : "/auth/login";
        const payload = mode === "signup" ? { email, password, name } : { email, password };
        const { data } = await api.post(url, payload);
        if (data.session_token) await setSessionToken(data.session_token);
        await refresh();
        // Save the session behind biometrics so next time the user can log
        // in with Face/Fingerprint. No-op on web / when unavailable.
        enableBiometric(email, data.session_token || getSessionToken()).catch(() => {});
        if (mode === "signup") toast.success(t("auth.welcome_new"));
        navigate("/dashboard");
      }
    } catch (err) {
      // Iter73 — Map the new 409 "google_only_account" backend code to a
      // localised, action-oriented message. Tester `emkaf79@gmail.com`
      // hit this because the account exists from a prior Google sign-in
      // but has no password on file.
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 409 && detail === "google_only_account") {
        toast.error(t("auth.google_only_account"), {
          duration: 6000,
          action: {
            label: t("auth.set_password"),
            onClick: () => setMode("reset"),
          },
        });
      } else {
        toast.error(detail || t("auth.auth_failed"));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleGoogle = async () => {
    // First preference on Android: native One-Tap plugin (single tap, no
    // browser, no deep-link round-trip). Falls back through to the older
    // browser-based flow if One-Tap isn't available or the user dismissed it.
    const onetapAvailable = isGoogleOneTapAvailable();
    // Diagnostic: always log this so we can verify the new code is in the
    // installed APK. Tester v56 reported "still 4 screens" — this tells us
    // immediately whether the One-Tap branch was even reached.
    try { console.warn("[login] handleGoogle entered. onetapAvailable=", onetapAvailable, "clientId=", process.env.REACT_APP_GOOGLE_WEB_CLIENT_ID ? "set" : "missing"); } catch (_) {}
    if (onetapAvailable) {
      const res = await signInWithGoogleNative();
      try { console.warn("[login] one-tap result:", JSON.stringify(res)); } catch (_) {}
      if (res.ok) {
        const me = await refresh();
        // Iter96 — stash the session behind biometrics for Google logins too
        // (previously only email/pw enabled it). No-op on web / unavailable.
        enableBiometric(me?.user?.email, getSessionToken()).catch(() => {});
        navigate("/dashboard");
        return;
      }
      // Plugin available but sign-in was dismissed/cancelled by user.
      // Don't auto-fall-through to a browser tab — they explicitly closed
      // the prompt. Tester complaint v54: "I had to sign in twice".
      //
      // Iter76d — When the backend EXCHANGE fails (`backend-error`) we
      // ALSO must not fall through to the legacy browser flow. Tester
      // hit a white screen on `emkaf79@gmail.com` because that account's
      // legacy DB record had a non-JSON-serialisable field → 500 →
      // frontend silently switched to `startNativeGoogleSignIn`, opened
      // a fresh OAuth tab, and the resulting in-app browser landed on
      // an unresponsive blank route. Now we surface the error directly.
      if (res.reason === "backend-error") {
        toast.error(t("auth.auth_failed") + (res.detail ? `: ${String(res.detail).slice(0, 80)}` : ""));
        return;
      }
      if (["unavailable", "plugin-error", "no-id-token"].includes(res.reason)) {
        // Real plugin-level failure → try browser flow as last resort
        console.warn("[login] one-tap failed, falling back to browser:", res.reason, res.detail);
      } else {
        // User dismissed prompt — just stop, don't spawn browser.
        return;
      }
    }
    if (isNative()) {
      try {
        await startNativeGoogleSignIn(async (sessionId) => {
          const { data } = await api.post("/auth/session", { session_id: sessionId });
          if (data?.session_token) await setSessionToken(data.session_token);
          const me = await refresh();
          enableBiometric(me?.user?.email, data.session_token || getSessionToken()).catch(() => {});
          navigate("/dashboard");
        });
      } catch (err) {
        toast.error(t("auth.google_unavailable"));
      }
      return;
    }
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const heading = mode === "signup"
    ? t("auth.start_journey")
    : mode === "reset"
    ? t("auth.reset_password")
    : t("auth.welcome_back");

  const subhead = mode === "signup"
    ? t("auth.sub_signup")
    : mode === "reset"
    ? t("auth.sub_reset")
    : t("auth.sub_login");

  const submitLabel = submitting
    ? t("common.please_wait")
    : mode === "signup"
    ? t("auth.submit_signup")
    : mode === "reset"
    ? t("auth.submit_reset")
    : t("auth.submit_login");

  return (
    <div className="min-h-screen bg-[#F7F5F0] text-[#2C332A] flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-md my-auto">
        <Link to="/" className="flex items-center gap-2 mb-6 text-[#2C332A]/70 hover:text-[#2C332A]">
          <img src="/logo.png" alt="Fitjong" className="w-8 h-8 rounded-lg" />
          <span style={{ fontFamily: "Outfit, sans-serif" }} className="text-xl font-medium">
            Fitjong
          </span>
        </Link>

        <h1
          style={{ fontFamily: "Outfit, sans-serif" }}
          className="text-2xl sm:text-3xl font-light tracking-tight mb-1"
        >
          {heading}
        </h1>
        <p className="text-sm text-[#2C332A]/70 mb-5">{subhead}</p>

        {sessionExpired && (
          <div
            data-testid="session-expired-banner"
            className="mb-4 p-3 rounded-2xl bg-[#FFF4E5] border border-[#F5C77E] text-[#7A4F0F] text-sm"
          >
            {t("auth.session_expired")}
          </div>
        )}

        {bioReady && mode === "login" && (
          <Button
            data-testid="biometric-login-btn"
            onClick={doBiometricLogin}
            variant="outline"
            className="w-full h-12 rounded-full border-[#5F7161] text-[#5F7161] bg-white hover:bg-[#EFF1EC] mb-3"
          >
            <Fingerprint className="mr-2 h-5 w-5" />
            {t("auth.biometric_login_btn", "Log in with fingerprint / face")}
          </Button>
        )}

        {googleAvailable && mode !== "reset" && (
          <>
            <Button
              data-testid="google-signin-btn"
              onClick={handleGoogle}
              variant="outline"
              className="w-full h-12 rounded-full border-[#E6E2D8] bg-white hover:bg-[#F7F5F0]"
            >
              <svg className="mr-2 h-5 w-5" viewBox="0 0 48 48">
                <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.8 32.4 29.3 35.5 24 35.5 17.4 35.5 12 30.1 12 23.5S17.4 11.5 24 11.5c3 0 5.8 1.1 7.9 3l5.7-5.7C34.3 5.4 29.4 3.5 24 3.5 12.7 3.5 3.5 12.7 3.5 24S12.7 44.5 24 44.5 44.5 35.3 44.5 24c0-1.2-.1-2.4-.3-3.5z"/>
                <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.6 19 12.5 24 12.5c3 0 5.8 1.1 7.9 3l5.7-5.7C34.3 5.4 29.4 3.5 24 3.5 16.1 3.5 9.4 8 6.3 14.7z"/>
                <path fill="#4CAF50" d="M24 44.5c5.2 0 10-2 13.6-5.2l-6.3-5.2c-2 1.4-4.5 2.2-7.3 2.2-5.2 0-9.7-3.1-11.3-7.4l-6.5 5C9.2 40 16 44.5 24 44.5z"/>
                <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.1-4 5.5l6.3 5.2c-.4.4 7-5 7-14.7 0-1.2-.1-2.4-.3-3.5z"/>
              </svg>
              {t("auth.continue_with_google")}
            </Button>

            {/* Iter76c — Tester report: "Google login uses a different
                Gmail account, why?". `tryAutoOrOneTap` (default fast
                path) silently re-uses the device's primary account.
                This sub-link forces the system Credential Manager
                picker so users with multiple Google accounts can pick
                the right one explicitly. */}
            <button
              type="button"
              data-testid="google-switch-account-btn"
              onClick={async () => {
                const res = await signInWithGoogleNative({ forcePicker: true });
                if (res.ok) {
                  await refresh();
                  navigate("/dashboard");
                }
              }}
              className="text-xs text-[#2C332A]/55 hover:text-[#2C332A] underline underline-offset-2 mt-2 w-full text-center"
            >
              {t("auth.google_use_different_account")}
            </button>

            <div className="my-6 flex items-center gap-3 text-[#2C332A]/50 text-sm">
              <div className="flex-1 h-px bg-[#E6E2D8]" />
              <span>{t("auth.divider_or")}</span>
              <div className="flex-1 h-px bg-[#E6E2D8]" />
            </div>
          </>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {mode === "signup" && (
            <div>
              <Label htmlFor="name">{t("auth.name_label")}</Label>
              <Input
                data-testid="signup-name-input"
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                className="mt-1 h-11 rounded-xl border-[#E6E2D8] bg-white"
              />
            </div>
          )}
          <div>
            <Label htmlFor="email">{t("auth.email_label")}</Label>
            <Input
              data-testid="auth-email-input"
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="mt-1 h-11 rounded-xl border-[#E6E2D8] bg-white"
            />
          </div>
          <div>
            <Label htmlFor="password">
              {mode === "reset" ? t("auth.new_password_label") : t("auth.password_label")}
            </Label>
            <div className="relative mt-1">
              <Input
                data-testid="auth-password-input"
                id="password"
                type={showPassword ? "text" : "password"}
                minLength={6}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="h-11 rounded-xl border-[#E6E2D8] bg-white pr-11"
              />
              <button
                type="button"
                data-testid="toggle-password-visibility-btn"
                onClick={() => setShowPassword((v) => !v)}
                className="absolute inset-y-0 right-0 px-3 flex items-center text-[#2C332A]/60 hover:text-[#2C332A]"
                aria-label={showPassword ? t("auth.hide_password") : t("auth.show_password")}
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>
          {mode === "reset" && (
            <div>
              <Label htmlFor="confirm-password">{t("auth.confirm_password_label")}</Label>
              <div className="relative mt-1">
                <Input
                  data-testid="auth-confirm-password-input"
                  id="confirm-password"
                  type={showPassword ? "text" : "password"}
                  minLength={6}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  className="h-11 rounded-xl border-[#E6E2D8] bg-white pr-11"
                />
              </div>
            </div>
          )}

          {mode === "login" && (
            <div className="text-right -mt-2">
              <button
                type="button"
                data-testid="forgot-password-btn"
                onClick={() => { setMode("reset"); setPassword(""); setConfirmPassword(""); }}
                className="text-sm text-[#5F7161] underline"
              >
                {t("auth.forgot_password")}
              </button>
            </div>
          )}
          <Button
            data-testid="auth-submit-btn"
            type="submit"
            disabled={submitting}
            className="w-full h-12 rounded-full bg-[#5F7161] hover:bg-[#4D5D4F] text-white"
          >
            {submitLabel}
          </Button>
        </form>

        {mode === "reset" ? (
          <p className="mt-6 text-sm text-[#2C332A]/70 text-center">
            <button
              type="button"
              data-testid="back-to-login-btn"
              onClick={() => { setMode("login"); setPassword(""); setConfirmPassword(""); }}
              className="text-[#5F7161] underline"
            >
              {t("auth.back_to_signin")}
            </button>
          </p>
        ) : (
          <p className="mt-6 text-sm text-[#2C332A]/70 text-center">
            {mode === "signup" ? t("auth.already_have_account") : t("auth.no_account_yet")}{" "}
            <button
              data-testid="auth-toggle-mode-btn"
              onClick={() => setMode(mode === "signup" ? "login" : "signup")}
              className="text-[#5F7161] underline"
            >
              {mode === "signup" ? t("auth.submit_login") : t("nav.sign_up")}
            </button>
          </p>
        )}
        {/* Iter77 — tiny build tag in the corner so testers can verify
            which bundle is actually running on their phone. If you
            don't see "i77-tp" here, the JS bundle is stale and the
            APK is serving a pre-Iter77 cache. */}
        <p
          data-testid="build-tag"
          className="mt-3 text-[10px] text-[#2C332A]/30 text-center select-all"
        >
          build {BUILD_TAG}
        </p>
        {getSessionDiag() ? (
          <p
            data-testid="session-diag"
            className="mt-1 text-[10px] text-[#2C332A]/30 text-center select-all"
          >
            sess: {getSessionDiag()}
          </p>
        ) : null}
      </div>
    </div>
  );
}
